import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSerialStore } from '../stores/serialStore';
import { serialService } from '../services/serialService';

interface SerialLine {
  id: string;
  timestamp: Date;
  data: string;
  isComplete: boolean; // true if line ends with \n
}

/**
 * Escape non-printable characters for display (excluding \r which we ignore)
 */
const escapeNonPrintable = (str: string): string => {
  return str.split('').map(char => {
    const code = char.charCodeAt(0);

    // Printable ASCII characters (space to tilde)
    if (code >= 32 && code <= 126) {
      return char;
    }

    // Common escape sequences
    switch (code) {
      case 9: return '\\t';   // Tab
      case 10: return '\\n';  // Line feed (shown but handled separately)
      case 13: return '';     // Carriage return - ignore completely
      case 27: return '\\e';  // Escape
      default:
        // Show as hex for other non-printable characters
        return `\\x${code.toString(16).padStart(2, '0')}`;
    }
  }).join('');
};

export const SerialIO: React.FC = () => {
  const [lines, setLines] = useState<SerialLine[]>([]);
  const [currentLineBuffer, setCurrentLineBuffer] = useState<string>('');
  const isConnected = useSerialStore((state) => state.isConnected);
  const logEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new data arrives
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines, currentLineBuffer]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    // Set up serial data callback
    const handleSerialData = (chunk: string) => {
      // Filter out \r characters completely
      const filteredChunk = chunk.replace(/\r/g, '');

      setCurrentLineBuffer(prev => {
        const combined = prev + filteredChunk;

        // Check if we have any complete lines (ending with \n)
        const lines = combined.split('\n');

        // Last element is the incomplete line (or empty if chunk ended with \n)
        const incomplete = lines.pop() || '';

        // Add complete lines to the lines array
        if (lines.length > 0) {
          setLines(prevLines => {
            const timestamp = new Date();
            const baseTime = timestamp.getTime();
            const lastId = prevLines.length > 0
              ? parseInt(prevLines[prevLines.length - 1].id.split('-')[1] || '0')
              : 0;

            const newLines: SerialLine[] = lines.map((lineData, index) => ({
              id: `${baseTime}-${lastId + index + 1}`,
              timestamp,
              data: lineData,
              isComplete: true,
            }));

            const combined = [...prevLines, ...newLines];
            // Keep only last 100 lines
            return combined.slice(-100);
          });
        }

        // Return the incomplete portion as the current buffer
        return incomplete;
      });
    };

    const unsubscribe = serialService.onData(handleSerialData);

    return () => {
      unsubscribe();
    };
  }, [isConnected]);

  const handleClear = () => {
    setLines([]);
    setCurrentLineBuffer('');
  };

  const formatTimestamp = (date: Date): string => {
    const time = date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${time}.${ms}`;
  };

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={handleClear}
          startIcon={<DeleteIcon />}
        >
          Clear
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
        }}
      >
        {!isConnected ? (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Not connected. Serial data will appear here when connected.
          </Typography>
        ) : lines.length === 0 && !currentLineBuffer ? (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No data received yet...
          </Typography>
        ) : (
          <>
            {/* Display complete lines */}
            {lines.map((line) => (
              <Box
                key={line.id}
                sx={{
                  mb: 0.5,
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap',
                }}
              >
                <span style={{ color: '#666' }}>[{formatTimestamp(line.timestamp)}]</span>{' '}
                <span style={{ color: '#0a0' }}>{escapeNonPrintable(line.data)}</span>
              </Box>
            ))}

            {/* Display current incomplete line (updates in real-time) */}
            {currentLineBuffer && (
              <Box
                sx={{
                  mb: 0.5,
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap',
                  opacity: 0.8, // Slightly dimmed to show it's incomplete
                }}
              >
                <span style={{ color: '#666' }}>[{formatTimestamp(new Date())}]</span>{' '}
                <span style={{ color: '#0a0' }}>{escapeNonPrintable(currentLineBuffer)}</span>
                <span style={{ color: '#888' }}>▌</span> {/* Cursor indicator */}
              </Box>
            )}
          </>
        )}
        <div ref={logEndRef} />
      </Box>

      <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
        Lines update character-by-character. \r is ignored, \n creates new lines. Other non-printable chars are escaped (e.g., \t, \xHH)
      </Typography>
    </Box>
  );
};
